// ContentOsFM01Test — FM-01 Zero-Narration Tech
// Copy 01: Content OS (@guilhermeribeiro.ai)
// Total: 1380 frames @ 30fps = 46s
// Format Mode: FM-01 (Zero-Narration Tech, abhishek-style)
// EDL: test-fm01-content-os.yaml
// v6: editorial thinking visuals + ElevenLabs voice over

import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Sequence,
  Audio,
  staticFile,
} from "remotion";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {SPRINGS} from "../presets/editing-styles";
import {FilmGrain} from "../components/dimus/FilmGrain";

// ─── Total frames export ────────────────────────────────────────────────────
export const FM01_TOTAL = 1380;

// ─── Color system ───────────────────────────────────────────────────────────
const C = {
  bg: "#0A0A0A",
  white: "#FFFFFF",
  pain: "#EF4444",
  amber: "#F59E0B",
  teal: "#14B8A6",
  muted: "#A3A3A3",
} as const;

// ─── Fix 1: Dark background system with animated blobs ──────────────────────
// Blob color presets per scene type
const BlobColor = {
  pain: "#EF4444",
  solution: "#14B8A6",
  feature: "#F59E0B",
  proof: "#14B8A6",
  cta: "#F59E0B",
} as const;

const MeshBg: React.FC<{
  blobColor: string;
  blobOpacity?: number;
  speed?: number;
}> = ({blobColor, blobOpacity = 0.22, speed = 1}) => {
  const f = useCurrentFrame();

  // Animated blob positions using sin/cos drift
  const blob1X = 30 + Math.sin(f * 0.008 * speed) * 8;
  const blob1Y = 25 + Math.cos(f * 0.006 * speed) * 6;
  const blob2X = 70 + Math.cos(f * 0.007 * speed) * 10;
  const blob2Y = 60 + Math.sin(f * 0.005 * speed) * 8;
  const blob3X = 50 + Math.sin(f * 0.004 * speed) * 12;
  const blob3Y = 80 + Math.cos(f * 0.009 * speed) * 5;

  // Convert hex opacity to alpha — blobOpacity controls intensity
  const alphaHex = Math.round(blobOpacity * 255).toString(16).padStart(2, "0");

  return (
    <AbsoluteFill style={{background: C.bg, overflow: "hidden"}}>
      {/* Layer 2: 3 radial gradient blobs */}
      <div
        style={{
          position: "absolute",
          left: `${blob1X}%`,
          top: `${blob1Y}%`,
          width: "70%",
          height: "70%",
          transform: "translate(-50%, -50%)",
          background: `radial-gradient(circle, ${blobColor}${alphaHex} 0%, transparent 70%)`,
          filter: "blur(60px)",
          willChange: "left, top",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: `${blob2X}%`,
          top: `${blob2Y}%`,
          width: "60%",
          height: "60%",
          transform: "translate(-50%, -50%)",
          background: `radial-gradient(circle, ${blobColor}33 0%, transparent 65%)`,
          filter: "blur(80px)",
          willChange: "left, top",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: `${blob3X}%`,
          top: `${blob3Y}%`,
          width: "50%",
          height: "50%",
          transform: "translate(-50%, -50%)",
          background: `radial-gradient(circle, ${blobColor}22 0%, transparent 60%)`,
          filter: "blur(100px)",
          willChange: "left, top",
        }}
      />
      {/* Layer 4: Vignette */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.65) 100%)",
          pointerEvents: "none",
        }}
      />
    </AbsoluteFill>
  );
};

// ─── Fix 4: Floating particles (golden ratio distribution) ───────────────────
const FloatingParticles: React.FC = () => {
  const f = useCurrentFrame();
  return (
    <>
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const x = (i * 137.5) % 100;
        const y = (i * 97.3) % 100;
        const drift = Math.sin(f * 0.003 + i * 1.2) * 3;
        const opacity = 0.04 + (i % 3) * 0.01;
        const size = 4 + (i % 3) * 2;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${x}%`,
              top: `${y + drift}%`,
              width: size,
              height: size,
              background: "#ffffff",
              opacity,
              borderRadius: 1,
              willChange: "top",
              pointerEvents: "none",
            }}
          />
        );
      })}
    </>
  );
};

// ─── Rotating mascot badge (L8 — persistent) ────────────────────────────────
const RotatingBadge: React.FC<{pulsing?: boolean}> = ({pulsing = false}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  // Fix 1 — slower rotation (less distracting)
  const rotDeg = (f / fps) * 0.15 * 360;

  const entryP = spring({frame: f, fps, config: SPRINGS.snappy});
  const opacity = interpolate(entryP, [0, 1], [0, 1], CLAMP);

  const pulseScale = pulsing ? 1 + Math.sin(f * 0.15) * 0.08 : 1;

  return (
    <div
      style={{
        position: "absolute",
        top: 72,
        right: 60,
        width: 64,
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.amber,
        borderRadius: "50%",
        opacity,
        transform: `rotate(${rotDeg}deg) scale(${pulseScale})`,
        willChange: "transform, opacity",
        zIndex: 100,
        fontSize: 32,
        fontFamily: FONT_STACK,
      }}
    >
      ✳
    </div>
  );
};

// ─── Pill badge (spring pop-in) ──────────────────────────────────────────────
const Pill: React.FC<{
  text: string;
  bgColor: string;
  textColor: string;
  delay?: number;
  border?: string;
}> = ({text, bgColor, textColor, delay = 0, border}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const p = spring({frame: f - delay, fps, config: SPRINGS.bouncy});
  const scale = interpolate(p, [0, 1], [0.4, 1], CLAMP);
  const opacity = interpolate(p, [0, 0.3], [0, 1], CLAMP);

  if (f < delay) return null;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 8,
        paddingBottom: 8,
        background: bgColor,
        borderRadius: 20,
        border: border ?? "none",
        fontSize: 22,
        fontWeight: 600,
        fontFamily: FONT_STACK,
        color: textColor,
        letterSpacing: "0.03em",
        transform: `scale(${scale})`,
        opacity,
        willChange: "transform, opacity",
        transformOrigin: "left center",
      }}
    >
      {text}
    </div>
  );
};

// ─── Word-by-word reveal (spring) ────────────────────────────────────────────
const WordReveal: React.FC<{
  text: string;
  color: string;
  fontSize: number;
  fontWeight: number;
  delay?: number;
  stagger?: number;
  lineHeight?: number;
}> = ({text, color, fontSize, fontWeight, delay = 0, stagger = 8, lineHeight = 1.15}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const words = text.split(" ");

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: `0 ${fontSize * 0.26}px`,
        fontFamily: FONT_STACK,
        fontSize,
        fontWeight,
        color,
        lineHeight,
      }}
    >
      {words.map((word, i) => {
        const wordDelay = delay + i * stagger;
        const p = spring({frame: f - wordDelay, fps, config: SPRINGS.snappy});
        const ty = interpolate(p, [0, 1], [24, 0], CLAMP);
        const op = interpolate(p, [0, 0.4], [0, 1], CLAMP);
        return (
          <span
            key={i}
            style={{
              transform: `translateY(${ty}px)`,
              opacity: f < wordDelay ? 0 : op,
              display: "inline-block",
              willChange: "transform, opacity",
            }}
          >
            {word}
          </span>
        );
      })}
    </div>
  );
};

// ─── Spring slide (text from below) ─────────────────────────────────────────
const SlideUp: React.FC<{
  text: string;
  color: string;
  fontSize: number;
  fontWeight: number;
  delay?: number;
}> = ({text, color, fontSize, fontWeight, delay = 0}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: f - delay, fps, config: SPRINGS.snappy});
  const ty = interpolate(p, [0, 1], [40, 0], CLAMP);
  const op = interpolate(p, [0, 0.4], [0, 1], CLAMP);

  if (f < delay) return null;

  return (
    <div
      style={{
        fontFamily: FONT_STACK,
        fontSize,
        fontWeight,
        color,
        transform: `translateY(${ty}px)`,
        opacity: op,
        willChange: "transform, opacity",
        lineHeight: 1.1,
      }}
    >
      {text}
    </div>
  );
};

// ─── Strikethrough replace ────────────────────────────────────────────────────
const StrikeReplace: React.FC<{
  lines: Array<{text: string; color: string}>;
  resolveText: string;
  resolveColor: string;
  resolveDelay: number;
  fontSize: number;
  fontWeight: number;
  strikeDelay?: number;
}> = ({lines, resolveText, resolveColor, resolveDelay, fontSize, fontWeight, strikeDelay = 20}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const strikeP = spring({frame: f - strikeDelay, fps, config: SPRINGS.smooth});
  const lineWidth = interpolate(strikeP, [0, 1], [0, 100], CLAMP);

  const newP = spring({frame: f - resolveDelay, fps, config: SPRINGS.snappy});
  const newOp = interpolate(newP, [0, 1], [0, 1], CLAMP);
  const newTy = interpolate(newP, [0, 1], [18, 0], CLAMP);

  return (
    <div style={{display: "flex", flexDirection: "column", gap: 16}}>
      {lines.map((line, i) => (
        <div key={i} style={{position: "relative", display: "inline-block"}}>
          <span
            style={{
              fontFamily: FONT_STACK,
              fontSize,
              fontWeight,
              color: line.color,
              lineHeight: 1.2,
            }}
          >
            {line.text}
          </span>
          <div
            style={{
              position: "absolute",
              top: "52%",
              left: 0,
              height: 3,
              background: C.pain,
              width: `${lineWidth}%`,
              willChange: "width",
            }}
          />
        </div>
      ))}
      {f >= resolveDelay && (
        <div
          style={{
            fontFamily: FONT_STACK,
            fontSize,
            fontWeight,
            color: resolveColor,
            transform: `translateY(${newTy}px)`,
            opacity: newOp,
            willChange: "transform, opacity",
            lineHeight: 1.2,
          }}
        >
          {resolveText}
        </div>
      )}
    </div>
  );
};

// ─── CountUp number ──────────────────────────────────────────────────────────
const CountUpNum: React.FC<{
  from: number;
  to: number;
  prefix?: string;
  suffix?: string;
  color: string;
  fontSize: number;
  fontWeight: number;
  delay?: number;
  duration?: number;
}> = ({from, to, prefix = "", suffix = "", color, fontSize, fontWeight, delay = 0, duration = 45}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const progress = interpolate(f - delay, [0, duration], [0, 1], CLAMP);
  const eased = 1 - Math.pow(1 - progress, 2);
  const current = Math.round(from + (to - from) * eased);

  const entryP = spring({frame: f - delay, fps, config: SPRINGS.snappy});
  const op = interpolate(entryP, [0, 0.4], [0, 1], CLAMP);
  const sc = interpolate(entryP, [0, 1], [0.8, 1], CLAMP);

  if (f < delay) return null;

  return (
    <div
      style={{
        fontFamily: FONT_STACK,
        fontSize,
        fontWeight,
        color,
        opacity: op,
        transform: `scale(${sc})`,
        willChange: "transform, opacity",
        lineHeight: 1,
      }}
    >
      {prefix}{current}{suffix}
    </div>
  );
};

// ─── Timeline reveal (SC-007 sub-scene A) ────────────────────────────────────
const TimelineReveal: React.FC<{
  events: Array<{time: string; label: string; color: string; entryDelay: number}>;
}> = ({events}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  return (
    <div style={{display: "flex", flexDirection: "column", gap: 24}}>
      {events.map((ev, i) => {
        const p = spring({frame: f - ev.entryDelay, fps, config: SPRINGS.snappy});
        const tx = interpolate(p, [0, 1], [-60, 0], CLAMP);
        const op = interpolate(p, [0, 0.4], [0, 1], CLAMP);
        if (f < ev.entryDelay) return null;
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              transform: `translateX(${tx}px)`,
              opacity: op,
              willChange: "transform, opacity",
            }}
          >
            <div
              style={{
                fontFamily: FONT_STACK,
                fontSize: 52,
                fontWeight: 900,
                color: ev.color,
                minWidth: 100,
                lineHeight: 1,
              }}
            >
              {ev.time}
            </div>
            <div
              style={{
                width: 3,
                height: 52,
                background: ev.color,
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
            <div
              style={{
                fontFamily: FONT_STACK,
                fontSize: 36,
                fontWeight: 600,
                color: C.white,
                lineHeight: 1.2,
              }}
            >
              {ev.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Comment bubble (SC-008) ─────────────────────────────────────────────────
const CommentBubble: React.FC<{delay?: number}> = ({delay = 0}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: f - delay, fps, config: SPRINGS.bouncy});
  const sc = interpolate(p, [0, 1], [0.5, 1], CLAMP);
  const op = interpolate(p, [0, 0.3], [0, 1], CLAMP);
  if (f < delay) return null;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 12,
        background: "rgba(255,255,255,0.08)",
        border: `2px solid ${C.amber}`,
        borderRadius: 24,
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 16,
        paddingBottom: 16,
        transform: `scale(${sc})`,
        opacity: op,
        willChange: "transform, opacity",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: C.amber,
          flexShrink: 0,
        }}
      />
      <div
        style={{
          fontFamily: FONT_STACK,
          fontSize: 32,
          fontWeight: 700,
          color: C.white,
          letterSpacing: "0.04em",
        }}
      >
        SISTEMA
      </div>
    </div>
  );
};

// ─── StruckLine (static — already struck) ────────────────────────────────────
const StruckLine: React.FC<{text: string; fontSize: number}> = ({text, fontSize}) => (
  <div style={{position: "relative", display: "inline-block"}}>
    <span style={{
      fontSize,
      fontWeight: 700,
      color: `rgba(255,255,255,0.35)`,
      fontFamily: FONT_STACK,
      lineHeight: 1.15,
    }}>
      {text}
    </span>
    <div style={{
      position: "absolute",
      left: 0,
      top: "50%",
      width: "100%",
      height: 2.5,
      background: "#EF4444",
      transform: "translateY(-50%)",
    }} />
  </div>
);

// ─── MAIN COMPOSITION ────────────────────────────────────────────────────────

// ─── TerminalCard ─────────────────────────────────────────────────────────────
interface TerminalLine {
  command: string;
  value?: string;
  valueColor?: string;
  delay: number;
}

const TerminalCard: React.FC<{
  lines: TerminalLine[];
  title?: string;
  width?: number;
}> = ({lines, title = "claude-code · session/new", width = 580}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  return (
    <div style={{
      width,
      background: "rgba(15, 15, 20, 0.92)",
      borderRadius: 16,
      overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.08)",
      boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
      fontFamily: "'SF Mono', 'Fira Code', monospace",
    }}>
      {/* Title bar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        gap: 8,
      }}>
        <div style={{display:"flex", gap:6}}>
          {["#EF4444","#F59E0B","#22C55E"].map((c,i) => (
            <div key={i} style={{width:12,height:12,borderRadius:"50%",background:c,opacity:0.8}}/>
          ))}
        </div>
        <span style={{color:"rgba(255,255,255,0.3)", fontSize:12, marginLeft:"auto"}}>{title}</span>
      </div>
      {/* Lines */}
      <div style={{padding:"16px 20px", display:"flex", flexDirection:"column", gap:10}}>
        {lines.map((line, i) => {
          const p = spring({frame: f - line.delay, fps, config: {mass:0.5, damping:20, stiffness:200}});
          const opacity = interpolate(p, [0,1], [0,1], {extrapolateLeft:"clamp", extrapolateRight:"clamp"});
          const slideX = interpolate(p, [0,1], [-8,0], {extrapolateLeft:"clamp", extrapolateRight:"clamp"});
          if (f < line.delay) return null;
          return (
            <div key={i} style={{
              display:"flex",
              justifyContent:"space-between",
              opacity,
              transform: `translateX(${slideX}px)`,
              willChange: "opacity, transform",
            }}>
              <span style={{color:"rgba(255,255,255,0.55)", fontSize:14}}>{line.command}</span>
              {line.value && (
                <span style={{
                  color: line.valueColor || "#F59E0B",
                  fontSize:14,
                  fontWeight:600,
                }}>{line.value}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── StatsCard ────────────────────────────────────────────────────────────────
const StatsCard: React.FC<{
  metric: string;
  label: string;
  sublabel?: string;
  accentColor?: string;
  bars?: number[];
  delay?: number;
  width?: number;
}> = ({metric, label, sublabel, accentColor = "#14B8A6", bars = [], delay = 0, width = 560}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const p = spring({frame: f - delay, fps, config: {mass:0.8, damping:14, stiffness:120}});
  const opacity = interpolate(p, [0,1], [0,1], {extrapolateLeft:"clamp",extrapolateRight:"clamp"});
  const scale = interpolate(p, [0,1], [0.92,1], {extrapolateLeft:"clamp",extrapolateRight:"clamp"});
  const slideY = interpolate(p, [0,1], [24,0], {extrapolateLeft:"clamp",extrapolateRight:"clamp"});

  const maxBar = Math.max(...bars, 1);

  return (
    <div style={{
      width,
      opacity,
      transform: `scale(${scale}) translateY(${slideY}px)`,
      willChange: "opacity, transform",
    }}>
      <div style={{
        background: "rgba(255,255,255,0.04)",
        border: `1px solid rgba(255,255,255,0.08)`,
        borderRadius: 20,
        padding: "28px 32px",
        backdropFilter: "blur(20px)",
      }}>
        {/* Metric */}
        <div style={{display:"flex", alignItems:"baseline", gap:12, marginBottom:6}}>
          <span style={{fontSize:72, fontWeight:900, color:"#FFFFFF", lineHeight:1, fontFamily:"system-ui, sans-serif"}}>
            {metric}
          </span>
          {sublabel && (
            <span style={{fontSize:22, color:"rgba(255,255,255,0.4)", fontWeight:400}}>{sublabel}</span>
          )}
        </div>
        <div style={{fontSize:18, color:"rgba(255,255,255,0.5)", marginBottom:28}}>{label}</div>
        {/* Mini bar chart */}
        {bars.length > 0 && (
          <div style={{display:"flex", alignItems:"flex-end", gap:5, height:52}}>
            {bars.map((b, i) => {
              const barP = spring({frame: f - delay - i * 3, fps, config:{mass:0.6,damping:12,stiffness:140}});
              const h = interpolate(barP, [0,1], [0, (b/maxBar)*52], {extrapolateLeft:"clamp",extrapolateRight:"clamp"});
              const isRecent = i >= bars.length - 4;
              return (
                <div key={i} style={{
                  flex:1,
                  height: h,
                  background: isRecent ? accentColor : "rgba(255,255,255,0.15)",
                  borderRadius: "3px 3px 0 0",
                  transition: "none",
                  willChange: "height",
                }}/>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── FlowStep ─────────────────────────────────────────────────────────────────
const FlowStep: React.FC<{
  num: string;
  text: string;
  color?: string;
  delay?: number;
  active?: boolean;
}> = ({num, text, color = "#F59E0B", delay = 0, active = false}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const p = spring({frame: f - delay, fps, config:{mass:0.6, damping:14, stiffness:140}});
  const opacity = interpolate(p, [0,1], [0,1], {extrapolateLeft:"clamp",extrapolateRight:"clamp"});
  const slideX = interpolate(p, [0,1], [30,0], {extrapolateLeft:"clamp",extrapolateRight:"clamp"});

  return (
    <div style={{
      display:"flex",
      alignItems:"center",
      gap:18,
      opacity,
      transform: `translateX(${slideX}px)`,
      willChange: "opacity, transform",
      padding: "14px 20px",
      background: active ? `${color}14` : "rgba(255,255,255,0.03)",
      borderRadius: 12,
      border: active ? `1px solid ${color}40` : "1px solid rgba(255,255,255,0.05)",
    }}>
      <div style={{
        width:36, height:36, borderRadius:"50%",
        background: active ? color : "rgba(255,255,255,0.08)",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:14, fontWeight:700, color: active ? "#000" : "rgba(255,255,255,0.4)",
        flexShrink:0,
        fontFamily: "system-ui, sans-serif",
      }}>{num}</div>
      <span style={{
        fontSize:20, fontWeight: active ? 700 : 500,
        color: active ? "#FFFFFF" : "rgba(255,255,255,0.45)",
        fontFamily: "system-ui, sans-serif",
      }}>{text}</span>
      {active && (
        <div style={{
          marginLeft:"auto",
          width:8, height:8, borderRadius:"50%",
          background:color,
          boxShadow:`0 0 8px ${color}`,
        }}/>
      )}
    </div>
  );
};

// ─── v6 EDITORIAL COMPONENT 1: ContentGrid ───────────────────────────────────
// Instagram 3×3 grid — 6 filled thumbnails + 3 empty gaps, proves "you stopped posting"
const ContentGrid: React.FC<{delay?: number}> = ({delay = 0}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const cells = [
    {color:"#6366f1", filled:true},
    {color:"#8b5cf6", filled:true},
    {color:"#06b6d4", filled:true},
    {color:"#10b981", filled:true},
    {color:"#f59e0b", filled:true},
    {color:"#ef4444", filled:true},
    {color:"transparent", filled:false},
    {color:"transparent", filled:false},
    {color:"transparent", filled:false},
  ];

  return (
    <div style={{
      display:"grid",
      gridTemplateColumns:"repeat(3, 1fr)",
      gap:3,
      width:320,
      borderRadius:12,
      overflow:"hidden",
    }}>
      {cells.map((cell, i) => {
        const p = spring({frame: f - delay - i * 4, fps, config:{mass:0.6,damping:14,stiffness:140}});
        const op = interpolate(p,[0,1],[0,1],{extrapolateLeft:"clamp",extrapolateRight:"clamp"});
        const sc = interpolate(p,[0,1],[0.85,1],{extrapolateLeft:"clamp",extrapolateRight:"clamp"});
        return (
          <div key={i} style={{
            width:"100%",
            paddingBottom:"100%",
            position:"relative",
            opacity: op,
            transform:`scale(${sc})`,
            willChange:"opacity,transform",
          }}>
            <div style={{
              position:"absolute", inset:0,
              background: cell.filled
                ? `linear-gradient(135deg, ${cell.color}cc, ${cell.color}55)`
                : "rgba(255,255,255,0.04)",
              border: cell.filled ? "none" : "1px dashed rgba(255,255,255,0.12)",
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
            }}>
              {!cell.filled && (
                <span style={{fontSize:18,color:"rgba(255,255,255,0.15)"}}>+</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── v6 EDITORIAL COMPONENT 2: ReachGraph ────────────────────────────────────
// Bar chart with 72H drop marker — proves the -40% reach claim visually
const ReachGraph: React.FC<{delay?: number}> = ({delay = 0}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const barData = [45,52,48,55,50,35,28,22,18,15,12,10];
  const maxVal = Math.max(...barData);
  const dropIndex = 3;

  return (
    <div style={{width:560, padding:"20px 24px", background:"rgba(255,255,255,0.04)", borderRadius:16, border:"1px solid rgba(255,255,255,0.07)"}}>
      <div style={{fontSize:12, color:"rgba(255,255,255,0.35)", marginBottom:12, fontFamily:"system-ui,sans-serif", letterSpacing:"0.05em"}}>
        ALCANCE ORGÂNICO — ÚLTIMOS 12 DIAS
      </div>
      <div style={{display:"flex",alignItems:"flex-end",gap:6,height:80,position:"relative"}}>
        {barData.map((val, i) => {
          const barP = spring({frame: f - delay - i * 5, fps, config:{mass:0.7,damping:12,stiffness:140}});
          const h = interpolate(barP,[0,1],[0,(val/maxVal)*80],{extrapolateLeft:"clamp",extrapolateRight:"clamp"});
          const isDecline = i >= dropIndex;
          return (
            <div key={i} style={{flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2}}>
              <div style={{
                width:"100%",
                height:h,
                background: isDecline ? "#EF4444" : "#14B8A6",
                opacity: isDecline ? 0.7 : 0.9,
                borderRadius:"3px 3px 0 0",
                willChange:"height",
              }}/>
            </div>
          );
        })}
        {/* 72H marker */}
        {f > (delay + dropIndex * 5 + 10) && (
          <div style={{
            position:"absolute",
            left: `${(dropIndex/barData.length)*100 - 2}%`,
            top:0, bottom:0,
            width:1,
            background:"rgba(239,68,68,0.6)",
            borderLeft:"1px dashed rgba(239,68,68,0.8)",
          }}>
            <div style={{
              position:"absolute",
              top:-22,
              left:4,
              fontSize:10,
              color:"#EF4444",
              fontFamily:"system-ui,sans-serif",
              fontWeight:600,
              letterSpacing:"0.05em",
              whiteSpace:"nowrap",
            }}>72H SEM POST</div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ContentOsFM01v6: React.FC = () => {
  const f = useCurrentFrame();

  // SC-001: frames 0–89
  // SC-002: frames 90–239
  // SC-003: frames 240–389
  // SC-004: frames 390–509
  // SC-005: frames 510–749
  // SC-006: frames 750–929
  // SC-007: frames 930–1229 (sub-A 0-179, sub-B 180-299)
  // SC-008: frames 1230–1379
  // Total: 90+150+150+120+240+180+300+150 = 1380 ✓

  const SC = {
    s1: {from: 0, dur: 90},
    s2: {from: 90, dur: 150},
    s3: {from: 240, dur: 150},
    s4: {from: 390, dur: 120},
    s5: {from: 510, dur: 240},
    s6: {from: 750, dur: 180},
    s7: {from: 930, dur: 300},
    s8: {from: 1230, dur: 150},
  } as const;

  // Fix 2: shared layout constants — pill at ~10% top, hero centered ~40-45%
  const PILL_TOP = 110;      // ~8.5% of 1280px height
  const HERO_TOP = 460;      // ~36% of 1280px → visual center with pill above
  const SUB_TOP = 620;       // below hero

  return (
    <AbsoluteFill style={{background: C.bg, fontFamily: FONT_STACK}}>

      {/* ─── SC-001: PAIN HOOK LINE 1 ─────────────────────────────── */}
      <Sequence from={SC.s1.from} durationInFrames={SC.s1.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc01.mp3")} startFrom={0}/>
          {/* Fix 1: proper dark bg with pain blobs */}
          <MeshBg blobColor={BlobColor.pain} blobOpacity={0.20} speed={0.5} />
          <FilmGrain opacity={0.16} />
          {/* Fix 4: particles */}
          <FloatingParticles />
          {/* Fix 2: pill at top, hero centered */}
          <div style={{position: "absolute", top: PILL_TOP, left: 60}}>
            <Pill text="PROBLEMA" bgColor={C.pain} textColor={C.white} delay={0} />
          </div>
          {/* v6: hero text left side, ContentGrid right side */}
          <div style={{position: "absolute", left: 40, right: 340, top: 500, letterSpacing: "-0.025em"}}>
            <WordReveal
              text="Você parou de postar."
              color={C.white}
              fontSize={72}
              fontWeight={800}
              delay={8}
              stagger={10}
            />
          </div>
          {/* v6 editorial: ContentGrid shows the posting gap visually */}
          <div style={{position:"absolute", right:40, top:380}}>
            <ContentGrid delay={15}/>
          </div>
        </AbsoluteFill>
      </Sequence>

      {/* ─── SC-002: PAIN REFRAME ─────────────────────────────────── */}
      <Sequence from={SC.s2.from} durationInFrames={SC.s2.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc02.mp3")} startFrom={0}/>
          <MeshBg blobColor={BlobColor.pain} blobOpacity={0.20} speed={0.5} />
          <FilmGrain opacity={0.16} />
          <FloatingParticles />
          <div style={{position: "absolute", left: 60, right: 60, top: 380}}>
            <WordReveal
              text="Não porque"
              color={C.white}
              fontSize={40}
              fontWeight={400}
              delay={0}
              stagger={8}
            />
          </div>
          <div style={{position: "absolute", left: 60, right: 60, top: 450}}>
            <StrikeReplace
              lines={[{text: "faltou ideia.", color: C.white}]}
              resolveText="faltou tempo."
              resolveColor={C.white}
              strikeDelay={20}
              resolveDelay={55}
              fontSize={64}
              fontWeight={800}
            />
          </div>
          {/* v6 editorial: "47 rascunhos não publicados" badge proves the idea abundance */}
          <div style={{position:"absolute", left:60, top:680}}>
            <div style={{
              display:"inline-flex",
              alignItems:"center",
              gap:8,
              background:"rgba(245,158,11,0.1)",
              border:"1px solid rgba(245,158,11,0.25)",
              borderRadius:8,
              padding:"8px 16px",
              opacity: f > 80 ? 1 : 0,
            }}>
              <span style={{fontSize:24, fontWeight:700, color:"#F59E0B", fontFamily:"system-ui,sans-serif"}}>47</span>
              <span style={{fontSize:14, color:"rgba(255,255,255,0.5)", fontFamily:"system-ui,sans-serif"}}>rascunhos não publicados</span>
            </div>
          </div>
        </AbsoluteFill>
      </Sequence>

      {/* ─── SC-003: STAT URGENCY ────────────────────────────────── */}
      <Sequence from={SC.s3.from} durationInFrames={SC.s3.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc03.mp3")} startFrom={0}/>
          <MeshBg blobColor={BlobColor.pain} blobOpacity={0.22} speed={0.8} />
          <FilmGrain opacity={0.16} />
          <FloatingParticles />

          {/* Sub-shot A (0-59): setup tension */}
          <Sequence from={0} durationInFrames={60}>
            <AbsoluteFill>
              <div style={{position: "absolute", top: PILL_TOP, left: 60}}>
                <Pill text="URGÊNCIA" bgColor={C.pain} textColor={C.white} delay={0} />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: HERO_TOP}}>
                <WordReveal
                  text="E o algoritmo não espera."
                  color={C.white}
                  fontSize={52}
                  fontWeight={600}
                  delay={0}
                  stagger={6}
                />
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* Sub-shot B (60-149): v6 editorial — ReachGraph replaces big countup */}
          <Sequence from={60} durationInFrames={90}>
            <AbsoluteFill>
              <div style={{position: "absolute", top: PILL_TOP, left: 60}}>
                <Pill text="URGÊNCIA" bgColor={C.pain} textColor={C.white} delay={0} />
              </div>
              <div style={{position: "absolute", left: 60, top: 380}}>
                <Pill text="72H" bgColor={C.pain} textColor={C.white} delay={0} />
              </div>
              {/* v6: ReachGraph proves the -40% claim */}
              <div style={{position:"absolute", left:60, right:60, top:460}}>
                <ReachGraph delay={0}/>
              </div>
              <div style={{position:"absolute", left:60, right:60, top:700}}>
                <WordReveal
                  text="-40% de alcance. Automático."
                  color={C.pain}
                  fontSize={36}
                  fontWeight={700}
                  delay={25}
                  stagger={5}
                />
              </div>
            </AbsoluteFill>
          </Sequence>
        </AbsoluteFill>
      </Sequence>

      {/* ─── SC-004: SOLUTION REVEAL ─────────────────────────────── */}
      <Sequence from={SC.s4.from} durationInFrames={SC.s4.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc04.mp3")} startFrom={0}/>
          <MeshBg blobColor={BlobColor.solution} blobOpacity={0.25} speed={0.4} />
          <FilmGrain opacity={0.16} />
          <FloatingParticles />
          <div style={{position: "absolute", left: 60, top: PILL_TOP}}>
            <Pill text="01 / A SOLUÇÃO" bgColor={C.teal} textColor={C.bg} delay={0} />
          </div>
          {/* Fix 3: single-line hero 72px */}
          <div style={{position: "absolute", left: 60, right: 60, top: HERO_TOP}}>
            <SlideUp
              text="Content OS."
              color={C.teal}
              fontSize={72}
              fontWeight={900}
              delay={8}
            />
          </div>
          <div style={{position: "absolute", left: 60, right: 60, top: SUB_TOP}}>
            <WordReveal
              text="Um sistema que não precisa de você."
              color={C.white}
              fontSize={32}
              fontWeight={400}
              delay={55}
              stagger={6}
            />
          </div>
          {/* v5: TerminalCard showing system workflow */}
          <div style={{position:"absolute", left:50, right:50, top:700}}>
            <TerminalCard
              title="content-os · automação ativa"
              lines={[
                {command:"✓ copy gerado", value:"09:14", valueColor:"#14B8A6", delay:40},
                {command:"✓ vídeo renderizado", value:"09:31", valueColor:"#14B8A6", delay:55},
                {command:"✓ agendado — 18:00", value:"publicado", valueColor:"#F59E0B", delay:70},
              ]}
              width={580}
            />
          </div>
        </AbsoluteFill>
      </Sequence>

      {/* ─── SC-005: COMO FUNCIONA — 4 hard-cut sub-shots ───────── */}
      <Sequence from={SC.s5.from} durationInFrames={SC.s5.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc05.mp3")} startFrom={0}/>
          <MeshBg blobColor={BlobColor.feature} blobOpacity={0.20} speed={0.3} />
          <FilmGrain opacity={0.16} />
          <FloatingParticles />

          {/* Pill persistent across all sub-shots */}
          <div style={{position: "absolute", left: 60, top: PILL_TOP}}>
            <Pill text="02 / COMO FUNCIONA" bgColor={C.amber} textColor={C.bg} delay={0} />
          </div>

          {/* v5: Sub-shot A — FlowStep 01 active */}
          <Sequence from={0} durationInFrames={60}>
            <AbsoluteFill>
              <div style={{position:"absolute",left:50,right:50,top:350,display:"flex",flexDirection:"column",gap:8}}>
                <FlowStep num="01" text="Você aprova 1 ideia." color={C.amber} delay={4} active={true}/>
                <FlowStep num="02" text="Ele escreve o copy." color={C.amber} delay={10} active={false}/>
                <FlowStep num="03" text="Gera o vídeo." color={C.amber} delay={16} active={false}/>
                <FlowStep num="04" text="Agenda. Publica." color={C.teal} delay={22} active={false}/>
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* v5: Sub-shot B — FlowStep 02 active */}
          <Sequence from={60} durationInFrames={50}>
            <AbsoluteFill>
              <div style={{position:"absolute",left:50,right:50,top:350,display:"flex",flexDirection:"column",gap:8}}>
                <FlowStep num="01" text="Você aprova 1 ideia." color={C.amber} delay={0} active={false}/>
                <FlowStep num="02" text="Ele escreve o copy." color={C.amber} delay={0} active={true}/>
                <FlowStep num="03" text="Gera o vídeo." color={C.amber} delay={0} active={false}/>
                <FlowStep num="04" text="Agenda. Publica." color={C.teal} delay={0} active={false}/>
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* v5: Sub-shot C — FlowStep 03 active */}
          <Sequence from={110} durationInFrames={50}>
            <AbsoluteFill>
              <div style={{position:"absolute",left:50,right:50,top:350,display:"flex",flexDirection:"column",gap:8}}>
                <FlowStep num="01" text="Você aprova 1 ideia." color={C.amber} delay={0} active={false}/>
                <FlowStep num="02" text="Ele escreve o copy." color={C.amber} delay={0} active={false}/>
                <FlowStep num="03" text="Gera o vídeo." color={C.amber} delay={0} active={true}/>
                <FlowStep num="04" text="Agenda. Publica." color={C.teal} delay={0} active={false}/>
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* v5: Sub-shot D — FlowStep 04 active */}
          <Sequence from={160} durationInFrames={80}>
            <AbsoluteFill>
              <div style={{position:"absolute",left:50,right:50,top:350,display:"flex",flexDirection:"column",gap:8}}>
                <FlowStep num="01" text="Você aprova 1 ideia." color={C.amber} delay={0} active={false}/>
                <FlowStep num="02" text="Ele escreve o copy." color={C.amber} delay={0} active={false}/>
                <FlowStep num="03" text="Gera o vídeo." color={C.amber} delay={0} active={false}/>
                <FlowStep num="04" text="Agenda. Publica." color={C.teal} delay={4} active={true}/>
              </div>
            </AbsoluteFill>
          </Sequence>
        </AbsoluteFill>
      </Sequence>

      {/* ─── SC-006: SEM EQUIPE — 4 sub-shots (rapid strikes) ────── */}
      <Sequence from={SC.s6.from} durationInFrames={SC.s6.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc06.mp3")} startFrom={0}/>
          <MeshBg blobColor={BlobColor.feature} blobOpacity={0.18} speed={0.3} />
          <FilmGrain opacity={0.16} />
          <FloatingParticles />
          <div style={{position: "absolute", left: 60, top: PILL_TOP}}>
            <Pill
              text="03 / SEM EQUIPE"
              bgColor="transparent"
              textColor={C.amber}
              delay={0}
              border={`1px solid ${C.amber}`}
            />
          </div>

          {/* Sub-shot A (0-39): "Sem editor." → strike */}
          <Sequence from={0} durationInFrames={40}>
            <AbsoluteFill>
              <div style={{position: "absolute", left: 60, right: 60, top: 500}}>
                <StrikeReplace
                  lines={[{text: "Sem editor.", color: C.white}]}
                  resolveText=""
                  resolveColor={C.white}
                  strikeDelay={8}
                  resolveDelay={9999}
                  fontSize={60}
                  fontWeight={700}
                />
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* Sub-shot B (40-79): [struck] + "Sem copywriter." → strike */}
          <Sequence from={40} durationInFrames={40}>
            <AbsoluteFill>
              <div style={{position: "absolute", left: 60, right: 60, top: 420}}>
                <StruckLine text="Sem editor." fontSize={52} />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: 500}}>
                <StrikeReplace
                  lines={[{text: "Sem copywriter.", color: C.white}]}
                  resolveText=""
                  resolveColor={C.white}
                  strikeDelay={8}
                  resolveDelay={9999}
                  fontSize={60}
                  fontWeight={700}
                />
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* Sub-shot C (80-119): 2×[struck] + "Sem social media." → strike */}
          <Sequence from={80} durationInFrames={40}>
            <AbsoluteFill>
              <div style={{position: "absolute", left: 60, right: 60, top: 350}}>
                <StruckLine text="Sem editor." fontSize={48} />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: 420}}>
                <StruckLine text="Sem copywriter." fontSize={48} />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: 500}}>
                <StrikeReplace
                  lines={[{text: "Sem social media.", color: C.white}]}
                  resolveText=""
                  resolveColor={C.white}
                  strikeDelay={8}
                  resolveDelay={9999}
                  fontSize={60}
                  fontWeight={700}
                />
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* Sub-shot D (120-179): 3×[struck] → resolve "1 pessoa. 1 sistema." */}
          <Sequence from={120} durationInFrames={60}>
            <AbsoluteFill>
              <div style={{position: "absolute", left: 60, right: 60, top: 310}}>
                <StruckLine text="Sem editor." fontSize={42} />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: 370}}>
                <StruckLine text="Sem copywriter." fontSize={42} />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: 430}}>
                <StruckLine text="Sem social media." fontSize={42} />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: 540}}>
                <SlideUp
                  text="1 pessoa. 1 sistema."
                  color={C.teal}
                  fontSize={64}
                  fontWeight={800}
                  delay={8}
                />
              </div>
            </AbsoluteFill>
          </Sequence>
        </AbsoluteFill>
      </Sequence>

      {/* ─── SC-007: VÊ AO VIVO + OS NÚMEROS ────────────────────── */}
      <Sequence from={SC.s7.from} durationInFrames={SC.s7.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc07.mp3")} startFrom={0}/>
          <MeshBg blobColor={BlobColor.proof} blobOpacity={0.22} speed={0.4} />
          <FilmGrain opacity={0.16} />
          <FloatingParticles />

          {/* Sub-scene A (frames 0–179): Timeline */}
          <Sequence from={0} durationInFrames={180}>
            <AbsoluteFill>
              <div style={{position: "absolute", left: 60, top: PILL_TOP}}>
                <Pill text="04 / VÊ AO VIVO" bgColor={C.teal} textColor={C.bg} delay={0} />
              </div>
              {/* Fix 2: timeline centered */}
              <div style={{position: "absolute", left: 60, right: 60, top: 420}}>
                <TimelineReveal
                  events={[
                    {time: "09:12", label: "ideia aprovada", color: C.amber, entryDelay: 20},
                    {time: "09:47", label: "reel publicado", color: C.teal, entryDelay: 80},
                  ]}
                />
              </div>
              <div style={{position: "absolute", left: 60, right: 60, top: 750}}>
                <WordReveal
                  text="35 minutos. Automático."
                  color={C.white}
                  fontSize={36}
                  fontWeight={600}
                  delay={130}
                  stagger={6}
                />
              </div>
            </AbsoluteFill>
          </Sequence>

          {/* Sub-scene B (frames 180–299): v5 — StatsCard + CountUp */}
          <Sequence from={180} durationInFrames={120}>
            <AbsoluteFill>
              <div style={{position: "absolute", left: 60, top: PILL_TOP}}>
                <Pill text="05 / OS NÚMEROS" bgColor={C.amber} textColor={C.bg} delay={0} />
              </div>
              <div style={{position:"absolute", left:50, right:50, top:300}}>
                <StatsCard
                  metric="47"
                  label="reels publicados · 1 aprovação/semana"
                  sublabel="reel/sem"
                  accentColor={C.teal}
                  bars={[8,12,10,15,11,14,18,16,20,22,25,24,28,30,32,35,38,36,40,42,47]}
                  delay={10}
                  width={620}
                />
              </div>
              <div style={{position:"absolute", left:60, right:60, top:760}}>
                <CountUpNum
                  from={0}
                  to={312}
                  prefix="+"
                  suffix="%"
                  color={C.teal}
                  fontSize={80}
                  fontWeight={900}
                  delay={40}
                  duration={40}
                />
              </div>
              <div style={{position:"absolute", left:60, right:60, top:870}}>
                <WordReveal
                  text="alcance orgânico"
                  color={C.muted}
                  fontSize={28}
                  fontWeight={400}
                  delay={55}
                  stagger={5}
                />
              </div>
            </AbsoluteFill>
          </Sequence>
        </AbsoluteFill>
      </Sequence>

      {/* ─── SC-008: CTA ─────────────────────────────────────────── */}
      <Sequence from={SC.s8.from} durationInFrames={SC.s8.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/v6/sc08.mp3")} startFrom={0}/>
          <MeshBg blobColor={BlobColor.cta} blobOpacity={0.22} speed={0.2} />
          <FilmGrain opacity={0.10} />
          <FloatingParticles />
          <div style={{position: "absolute", left: 60, top: PILL_TOP}}>
            <Pill text="06 / QUER ISSO?" bgColor={C.amber} textColor={C.bg} delay={0} />
          </div>
          {/* Centered CTA block */}
          <AbsoluteFill style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: 60,
            paddingLeft: 40,
            paddingRight: 40,
          }}>
            <div style={{textAlign: "center", width: "100%"}}>
              <WordReveal
                text="QUER ESSE SISTEMA?"
                color={C.white}
                fontSize={64}
                fontWeight={800}
                delay={0}
                stagger={7}
              />
            </div>
            <div style={{textAlign: "center", width: "100%", marginTop: 24}}>
              <SlideUp
                text="Comenta SISTEMA"
                color={C.amber}
                fontSize={72}
                fontWeight={900}
                delay={20}
              />
            </div>
            <div style={{marginTop: 48}}>
              <CommentBubble delay={90} />
            </div>
            <div style={{textAlign: "center", width: "100%", marginTop: 20}}>
              <WordReveal
                text="→ te mando o passo a passo."
                color={C.white}
                fontSize={32}
                fontWeight={400}
                delay={80}
                stagger={5}
              />
            </div>
          </AbsoluteFill>
          <RotatingBadge pulsing={true} />
        </AbsoluteFill>
      </Sequence>

      {/* ─── L8 Mascot (persistent — all scenes except SC-008) ─────── */}
      {f < SC.s8.from && <RotatingBadge pulsing={false} />}

    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
